import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  CashSpecialIcon,
  GradientText,
  Layout,
  PageTitle,
  RoundedCheckIcon,
} from "lola-framework-ui-test";
import type { AxiosError } from "axios";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { APP_ROUTES } from "../constants";
import {
  createOperation,
  getQuote,
  getAllPaymentMethods,
  isDeadLinkError,
} from "../services/api";
import { FUNDING_METHOD_CATEGORY } from "../utils/catalog";
import {
  createWithFreshQuote,
  waitForPersistHydration,
} from "../utils/quoteCommit";
import { getEvents, safeCapture } from "../analytics";
import {
  clearFirstSendWarningCache,
  wasFirstSendWarningShown,
} from "../hooks/useFirstSendWarning";
import type { FundingMethod } from "../utils/catalog";
import { COUNTRY_CURRENCY } from "../hooks/useExchangeRate";
import { buildRecipientFromConfig } from "../utils/partyName";
import { isHttpUrl, returnToChat } from "../utils/returnToChat";
import {
  forgetAuthorizeCvv,
  peekAuthorizeCvv,
} from "../utils/fresh-cvv-alias";

const isSessionExpiredError = (err: unknown): boolean => {
  const axiosError = err as AxiosError<{ error?: { code?: string } }>;
  const errorCode = axiosError?.response?.data?.error?.code;
  return (
    isDeadLinkError(err) ||
    errorCode === "LINK_NOT_FOUND" ||
    axiosError?.response?.status === 404
  );
};

/**
 * Module-level guard preventing duplicate operation creation across
 * unmount/remount cycles (StrictMode, routing re-mounts).
 */
let isOperationInFlight = false;

const MIN_TRANSFER_VISIBLE_MS = 2000;
const COUNTDOWN_SECONDS = 3;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_TRANSFER_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

/**
 * Creates the operation from the persisted signed quote. Cash deposit
 * continues to how-to-deposit; other methods show the certified
 * "Sending… / Going back to WhatsApp" interstitial.
 */
export const SendingMoneyPage = memo(() => {
  const { t } = useTranslation();
  const { theme, setQuoteState } = useStore();
  const { session, updateSession } = useSessionStore();
  const { redirectTo } = useRouteWithToken();

  const [phase, setPhase] = useState<"creating" | "leaving">("creating");
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const visibleSinceRef = useRef(Date.now());

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("committing"));

    if (isOperationInFlight) {
      return;
    }
    isOperationInFlight = true;

    const resolveRoute = () => {
      const cfg = session.data?.config;
      const corridor = cfg?.defaultCorridor || cfg?.corridors?.[0];
      const sourceCountry = corridor?.originCountryCode || "US";
      const destinationCountry = corridor?.destinationCountryCode || "MX";
      const sourceCurrency =
        cfg?.sourceCurrency || COUNTRY_CURRENCY[sourceCountry] || "USD";
      return { sourceCountry, destinationCountry, sourceCurrency };
    };

    const executeOperation = async () => {
      try {
        await waitForPersistHydration(useStore.persist);
        const capturedQuoteState = useStore.getState().quoteState;
        const linkId = session.data?.linkId;
        const signedQuote = capturedQuoteState?.signedQuote;

        const fundingMethod = capturedQuoteState?.fundingMethod as
          | FundingMethod
          | undefined;
        const category = fundingMethod
          ? FUNDING_METHOD_CATEGORY[fundingMethod]
          : undefined;

        const fundingSkipsInstrument = category === "CASH";

        let fundingPaymentMethodId: string | undefined;

        const { paymentState, preparedFundingPaymentMethodId } =
          useStore.getState();

        if (!fundingSkipsInstrument) {
          if (preparedFundingPaymentMethodId) {
            fundingPaymentMethodId = preparedFundingPaymentMethodId;
          } else if (paymentState.payment?.cardId) {
            fundingPaymentMethodId = paymentState.payment.cardId;
          } else if (linkId) {
            try {
              const paymentMethods = await getAllPaymentMethods(linkId);
              if (category === "ACCOUNT") {
                const ba =
                  paymentMethods.bankAccounts.find(
                    (b) => b.isDefault && b.enabled,
                  ) || paymentMethods.bankAccounts.find((b) => b.enabled);
                fundingPaymentMethodId = ba?.paymentMethodId;
              }
            } catch (pmErr) {
              console.error(
                "[SendingMoneyPage] Failed to fetch payment methods:",
                pmErr,
              );
            }
          }
        }

        if (!linkId) {
          throw new Error("No linkId found in session");
        }
        if (!signedQuote || !capturedQuoteState) {
          throw new Error("No signed quote available");
        }
        if (!fundingSkipsInstrument && !fundingPaymentMethodId) {
          throw new Error("No funding payment method selected");
        }

        const recipient = buildRecipientFromConfig(
          session.data?.config?.recipient,
        );
        const fundingCvvToken =
          category === "CARD" && fundingPaymentMethodId
            ? peekAuthorizeCvv(fundingPaymentMethodId)
            : undefined;

        const { result: created, quote: usedQuote } =
          await createWithFreshQuote({
            quote: capturedQuoteState,
            route: resolveRoute(),
            getQuote,
            create: (freshSignedQuote) =>
              createOperation(linkId, {
                signedQuote: freshSignedQuote,
                ...(fundingPaymentMethodId
                  ? { fundingPaymentMethodId }
                  : {}),
                ...(fundingCvvToken ? { fundingCvvToken } : {}),
                ...(recipient ? { recipient } : {}),
              }),
          });
        setQuoteState(usedQuote);
        if (fundingCvvToken && fundingPaymentMethodId) {
          forgetAuthorizeCvv(fundingPaymentMethodId);
        }
        const current = useSessionStore.getState().session.data;
        if (current) {
          updateSession({
            ...current,
            config: {
              ...current.config,
              usage: {
                state: "consumed",
                operationId: created.operationId,
              },
            },
          });
        }
        safeCapture(getEvents().OPERATION_CREATED, {
          success: true,
          first_send_warning_shown: wasFirstSendWarningShown(),
          operation_id: created.operationId,
          quote_id: created.quoteId,
          status: created.status,
          send_amount: created.sendAmount,
          receive_amount: created.receiveAmount,
          exchange_rate: created.exchangeRate,
          total_cost: created.totalCost,
          funding_payment_method_id: fundingPaymentMethodId,
          funding_method: fundingMethod,
          payout_method: usedQuote.payoutMethod,
          capability_key: usedQuote.capabilityKey,
        });
        clearFirstSendWarningCache();

        await holdMinimumVisible(visibleSinceRef.current);

        if (fundingSkipsInstrument) {
          redirectTo(
            `${APP_ROUTES.HOW_TO_DEPOSIT_CASH_PAGE}?operationId=${encodeURIComponent(created.operationId)}`,
            { replace: true },
          );
          return;
        }

        setPhase("leaving");
      } catch (err) {
        const axiosError = err as AxiosError<{
          error?: { code?: string; message?: string };
        }>;
        console.error("[SendingMoneyPage] Failed to create operation:", {
          message: axiosError?.message,
          status: axiosError?.response?.status,
          serverErrorCode: axiosError?.response?.data?.error?.code,
          serverErrorMessage: axiosError?.response?.data?.error?.message,
        });

        if (isSessionExpiredError(err)) {
          return;
        }

        safeCapture(getEvents().OPERATION_CREATED, {
          success: false,
          first_send_warning_shown: wasFirstSendWarningShown(),
          error: err instanceof Error ? err.message : "Create operation failed",
          error_code: "OPERATION_CREATE_FAILED",
        });
        clearFirstSendWarningCache();

        redirectTo(APP_ROUTES.SERVICE_UNAVAILABLE_PAGE, { replace: true });
      } finally {
        isOperationInFlight = false;
      }
    };

    executeOperation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== "leaving") return;
    setSecondsLeft(COUNTDOWN_SECONDS);
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== "leaving" || secondsLeft > 0) return;
    const url = session.data?.config?.returnToChatUrl;
    if (isHttpUrl(url)) {
      returnToChat(url);
    }
  }, [phase, secondsLeft, session.data?.config?.returnToChatUrl]);

  const leaving = phase === "leaving";
  const chatUrl = session.data?.config?.returnToChatUrl;
  const showReturnCta = leaving && isHttpUrl(chatUrl);

  const colorConfig = leaving
    ? (theme.specialView ?? theme.whiteView)
    : theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    iconColors,
    backgroundIcon,
    title,
    subtitile,
    bodyCopy,
  } = colorConfig;

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={bodyCopy} />
        {leaving ? (
          <section className="flow-hero">
            <FlowHeroMark backgroundIcon={backgroundIcon}>
              <RoundedCheckIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("sending.goingBack")}
                highlightColor={title}
                secudnary={t("sending.toWhatsApp")}
                secudnaryColor={subtitile ?? title}
              />
            </section>
          </section>
        ) : (
          <section className="flow-hero">
            <FlowHeroMark
              showSpinner
              dropzoneColors={dropzoneColors}
              backgroundIcon={backgroundIcon}
            >
              <CashSpecialIcon size={60} colors={iconColors} />
            </FlowHeroMark>
            <section className="landing-container">
              <PageTitle
                highlight={t("transfer.transferring")}
                highlightColor={title}
                secudnary={t("transfer.yourMoney")}
                secudnaryColor={title}
              />
            </section>
          </section>
        )}
      </Layout.Content>
      {leaving ? (
        <Layout.Footer>
          <section className="footer-section">
            <GradientText
              textColor={bodyCopy}
              as="p"
              className="footer-text"
              aria-live="polite"
            >
              {t("sending.countdown", { count: secondsLeft })}
            </GradientText>
            {showReturnCta ? (
              <Button
                type="button"
                size="large"
                className="lola-return-chat"
                background={backgroundBtn}
                color={textColorBtn}
                onClick={() => returnToChat(chatUrl)}
              >
                {t("sending.toWhatsApp")}
              </Button>
            ) : null}
          </section>
        </Layout.Footer>
      ) : null}
    </AuraLayout>
  );
});
